export interface LegalSection {
  heading: string
  paragraphs: string[]
}

export interface LegalDocument {
  title: string
  effectiveDate: string
  intro: string
  sections: LegalSection[]
}

export const PRIVACY_POLICY: LegalDocument = {
  title: "Privacy Policy",
  effectiveDate: "August 26, 2026",
  intro:
    "This Privacy Policy explains how ListYourProduct collects, uses, discloses, and protects your information when you use our website and services.",
  sections: [
    {
      heading: "Information We Collect",
      paragraphs: [
        "We collect information you provide directly, including your name, email address, account profile information, and content you submit while using ListYourProduct.",
        "We also collect technical and usage data such as device information, log data, and page activity to operate and improve our services.",
        "If you connect an Amazon selling account, we receive your Amazon selling partner identifier, selected marketplace, OAuth authorization token, and the product listing data you choose to send to Amazon. We do not request Amazon buyer, order, shipping-address, or other restricted personal data for the product-publishing feature."
      ]
    },
    {
      heading: "Amazon Selling Partner Integration",
      paragraphs: [
        "We use your Amazon authorization only to validate and publish product listings that you initiate. ListYourProduct requests the minimum Product Listing access needed for this feature and does not use the connection for unrelated purposes.",
        "Amazon refresh tokens are encrypted with authenticated encryption before storage and are kept in a server-only data path that website clients cannot read. Short-lived Amazon access tokens are requested only when needed and are not stored in the browser.",
        "You can disconnect Amazon from My Listings at any time. Disconnecting deletes the stored Amazon credential and connection record from ListYourProduct. You can also revoke access from Amazon Seller Central."
      ]
    },
    {
      heading: "How We Use Information",
      paragraphs: [
        "We use your information to provide, maintain, and improve ListYourProduct features, including generating product listings and related AI-powered outputs.",
        "We may use your contact information to send service updates, security notices, account communications, and support responses."
      ]
    },
    {
      heading: "Payments and Credits",
      paragraphs: [
        "Payments are processed through Stripe. We do not store your full payment card details on our servers.",
        "Image credits purchased are one-time purchases and never expire. Credit balances are stored on your account for use at your own pace."
      ]
    },
    {
      heading: "Data Sharing",
      paragraphs: [
        "We may share data with trusted third-party service providers that help us run ListYourProduct, such as hosting, analytics, authentication, and payment services.",
        "When you validate or publish an Amazon listing, we transmit the listing information you supplied to Amazon through the Selling Partner API. Amazon processes that information under its own terms and privacy practices.",
        "We may disclose information if required by law or to protect the rights, safety, and integrity of ListYourProduct and its users."
      ]
    },
    {
      heading: "Data Retention and Security",
      paragraphs: [
        "We retain information for as long as necessary to provide services, comply with legal obligations, and resolve disputes.",
        "Amazon credentials are retained while your store remains connected and are deleted when you disconnect the integration or when the associated ListYourProduct account is deleted, subject to limited backup and legal-retention requirements.",
        "We use encryption in transit, application-level encryption for Amazon refresh tokens, access controls, and other administrative and technical safeguards. No method of transmission or storage is completely secure."
      ]
    },
    {
      heading: "Your Choices",
      paragraphs: [
        "You can review and update certain account information from your profile settings.",
        "You may request account deletion or privacy-related support by contacting us at reymishra08@gmail.com."
      ]
    },
    {
      heading: "Contact Us",
      paragraphs: [
        "If you have questions about this Privacy Policy, please contact us at reymishra08@gmail.com.",
        "Website: https://listyourproduct.com"
      ]
    }
  ]
}

export const TERMS_OF_USE: LegalDocument = {
  title: "Terms of Use",
  effectiveDate: "August 26, 2026",
  intro:
    "These Terms of Use govern your access to and use of ListYourProduct. By using ListYourProduct, you agree to these terms.",
  sections: [
    {
      heading: "Eligibility and Accounts",
      paragraphs: [
        "You are responsible for maintaining the confidentiality of your account credentials and for activity under your account.",
        "You agree to provide accurate information and keep your account details up to date."
      ]
    },
    {
      heading: "Permitted Use",
      paragraphs: [
        "ListYourProduct is provided to help generate marketplace-ready product listing content, validate Amazon listing submissions, and publish listings at your direction.",
        "You agree not to misuse the service, interfere with platform operations, or use the service for unlawful or prohibited activities."
      ]
    },
    {
      heading: "Amazon Store Connection and Listings",
      paragraphs: [
        "You must have an eligible Amazon selling account and authority to connect it. You are responsible for maintaining marketplace eligibility, category approvals, brand authorization, intellectual-property rights, tax settings, product identifiers, and any identifier exemption you claim.",
        "Amazon validates and controls listing acceptance. A successful API submission does not guarantee that a listing will be immediately active, searchable, or approved. You must review Amazon's returned issues and your Seller Central account.",
        "You may disconnect the integration at any time. ListYourProduct is an independent service and is not endorsed by, affiliated with, or sponsored by Amazon. Amazon and Seller Central are trademarks of their respective owner."
      ]
    },
    {
      heading: "User Content and Responsibility",
      paragraphs: [
        "You are responsible for content you upload or generate, including ensuring that it does not violate third-party rights or platform policies.",
        "You should review AI-generated output before publishing it to marketplaces such as Amazon or Meesho."
      ]
    },
    {
      heading: "Credits and Purchases",
      paragraphs: [
        "ListYourProduct may provide trial AI image credits. Current credit-pack quantities and prices are displayed on the Pricing page before purchase.",
        "Purchased credits are non-refundable once used. Unused credits do not expire."
      ]
    },
    {
      heading: "Service Availability",
      paragraphs: [
        "We may modify, suspend, or discontinue features at any time to maintain, improve, or secure the service.",
        "We do not guarantee uninterrupted or error-free service availability."
      ]
    },
    {
      heading: "Limitation of Liability",
      paragraphs: [
        'To the extent permitted by law, ListYourProduct is provided on an "as is" and "as available" basis without warranties of any kind.',
        "ListYourProduct and its operators are not liable for indirect, incidental, special, or consequential damages arising from your use of the service."
      ]
    },
    {
      heading: "Contact Us",
      paragraphs: [
        "For questions about these Terms of Use, contact reymishra08@gmail.com.",
        "Website: https://listyourproduct.com"
      ]
    }
  ]
}
